import React from 'react';
import { Helmet } from 'react-helmet';


const PageHeaders = ({
  title,
  description = '',
  keywords = '',
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  ogImage = 'https://www.plootus.com/logo.png',
  twitterCard = 'summary_large_image',
  twitterSite = '@plootus',
  twitterCreator,
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonicalUrl,
  robots = 'index, follow',
  additionalMeta = {},
  schema = null,
}) => {
  // Default ogTitle and ogDescription to title and description if not provided
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;

  // Twitter Card defaults (fall back to OG tags, then title/description)
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || ogImage;

  // Generate clean URL without query parameters or hash
  // This ensures consistent canonical URLs and og:url across different URL variations
  // Example: https://www.plootus.com/page?utm_source=email → https://www.plootus.com/page
  const getCleanUrl = () => {
    if (typeof window === 'undefined') {
      return 'https://www.plootus.com'; // fallback
    }
    return window.location.origin + window.location.pathname;
  };

  // Auto-generate ogUrl if not provided
  const finalOgUrl = ogUrl || getCleanUrl();

  // Auto-generate canonicalUrl if not provided
  const finalCanonicalUrl = canonicalUrl || getCleanUrl();

  // Define default schemas
  const defaultSchemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Plootus',
      url: 'https://www.plootus.com',
      logo: 'https://www.plootus.com/logo.png',
      sameAs: [
        'https://twitter.com/plootus',
        'https://www.linkedin.com/company/plootus',
        'https://www.facebook.com/plootus',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Plootus',
      url: 'https://www.plootus.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.plootus.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  ];

  // Combine default schemas with any provided schema prop
  const combinedSchemas = [...defaultSchemas];
  if (schema) {
    if (Array.isArray(schema)) {
      combinedSchemas.push(...schema);
    } else {
      combinedSchemas.push(schema);
    }
  }

  return (
    <Helmet>
      {/* Title - Required for SEO and browser tab display */}
      <title>{title}</title>
      
      {/* Basic Meta Tags */}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* 
        Robots Meta Tag
        Controls search engine crawling and indexing.
        Default: 'index, follow' - allows search engines to index and follow links.
        Override with 'noindex, nofollow' for pages that shouldn't be indexed (e.g., thank you pages, admin pages).
      */}
      <meta name="robots" content={robots} />
      
      {/* Open Graph Meta Tags - For social media sharing (Facebook, LinkedIn, etc.) */}
      <meta property="og:title" content={finalOgTitle} />
      {finalOgDescription && <meta property="og:description" content={finalOgDescription} />}
      <meta property="og:type" content={ogType} />
      {finalOgUrl && <meta property="og:url" content={finalOgUrl} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* 
        Twitter Card Meta Tags
        Controls how content appears when shared on Twitter/X.
        - summary_large_image: Large featured image (recommended for most pages)
        - summary: Small thumbnail image
        Twitter defaults to Open Graph tags if Twitter-specific tags are missing,
        but we explicitly set them for better control and reliability.
      */}
      <meta name="twitter:card" content={twitterCard} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      <meta name="twitter:title" content={finalTwitterTitle} />
      {finalTwitterDescription && <meta name="twitter:description" content={finalTwitterDescription} />}
      {finalTwitterImage && <meta name="twitter:image" content={finalTwitterImage} />}
      
      {/* 
        Canonical URL
        Prevents duplicate content issues by specifying the preferred URL.
        Automatically excludes query parameters to ensure consistency.
        Example: /page?ref=email and /page?ref=social both point to /page
      */}
      {finalCanonicalUrl && <link rel="canonical" href={finalCanonicalUrl} />}
      
      {/* Additional Custom Meta Tags */}
      {Object.entries(additionalMeta).map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}

      {/* Structured Data (JSON-LD) */}
      {combinedSchemas.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(combinedSchemas.length === 1 ? combinedSchemas[0] : combinedSchemas)}
        </script>
      )}
    </Helmet>
  );
};

export default PageHeaders;
